import React, { useState, useMemo, memo, useCallback } from 'react';
import { LineChart as ChartIcon } from 'lucide-react';
import {
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import {
  BarTemplate,
  LineTemplate,
  ScatterTemplate,
  HistogramTemplate,
  SankeyTemplate,
  CHART_COLORS
} from './charts/ChartTemplates';

export const DynamicChart = memo(({ data, spec, noContainer = false }) => {
  const [hiddenSeries, setHiddenSeries] = useState(new Set());

  const handleLegendClick = useCallback((o) => {
    const { dataKey } = o;
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(dataKey)) next.delete(dataKey);
      else next.add(dataKey);
      return next;
    });
  }, []);

  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data) || !spec) return [];
    const { plot_type, x, y, grouping } = spec;

    if (plot_type === 'sankey') {
      // Process data for Sankey diagram: convert to nodes and links format
      // For sankey: x -> source, y -> target, grouping -> value
      if (!x || !y || !grouping) return { nodes: [], links: [] };

      const nodeMap = new Map();
      let nodeIndex = 0;

      // First pass: collect all unique nodes
      data.forEach(row => {
        const sourceVal = String(row[x] || '');
        const targetVal = String(row[y] || '');

        if (sourceVal && !nodeMap.has(sourceVal)) {
          nodeMap.set(sourceVal, nodeIndex++);
        }
        if (targetVal && !nodeMap.has(targetVal)) {
          nodeMap.set(targetVal, nodeIndex++);
        }
      });

      // Create nodes array
      const nodes = Array.from(nodeMap.keys()).map(name => ({ name }));

      // Create links array
      const links = data
        .filter(row => {
          const sourceVal = String(row[x] || '');
          const targetVal = String(row[y] || '');
          const val = Number(row[grouping]) || 0;
          return sourceVal && targetVal && val > 0;
        })
        .map(row => {
          const sourceVal = String(row[x] || '');
          const targetVal = String(row[y] || '');
          const val = Number(row[grouping]) || 0;
          return {
            source: nodeMap.get(sourceVal),
            target: nodeMap.get(targetVal),
            value: val
          };
        });

      return { nodes, links };
    }

    if (plot_type === 'hist') {
      const col = y ?? x; // y is the histogram column per spec; x is fallback
      if (!col) return [];
      const allValues = data.map(r => {
        const v = r[col];
        if (typeof v === 'number') return isNaN(v) ? undefined : v;
        if (typeof v === 'string' && v.trim() !== '') { const n = Number(v); return isNaN(n) ? undefined : n; }
        return undefined;
      }).filter(v => v !== undefined);
      if (allValues.length === 0) return [];

      let min = Infinity;
      let max = -Infinity;
      for (const v of allValues) {
        if (v < min) min = v;
        if (v > max) max = v;
      }

      if (min === Infinity || max === -Infinity) return [];

      const isInteger = allValues.every(v => Number.isInteger(v));
      const range = max - min;
      const n = allValues.length;

      // Freedman-Diaconis rule: binWidth = 2 * IQR * n^(-1/3)
      // Falls back to Sturges' rule (ceil(log2(n)+1)) when IQR is 0
      const sorted = [...allValues].sort((a, b) => a - b);
      const q1 = sorted[Math.floor(n * 0.25)];
      const q3 = sorted[Math.floor(n * 0.75)];
      const iqr = q3 - q1;

      let binCount, binWidth;

      if (isInteger && range < 25) {
        // Discrete integers with small range: one bin per value
        binCount = range + 1;
        binWidth = 1;
      } else if (iqr > 0) {
        // Freedman-Diaconis
        binWidth = 2 * iqr * Math.pow(n, -1 / 3);
        if (isInteger) binWidth = Math.max(1, Math.ceil(binWidth));
        binCount = Math.ceil(range / binWidth);
      } else {
        // Sturges fallback (degenerate IQR)
        binCount = Math.ceil(Math.log2(n) + 1);
        binWidth = range / binCount || 1;
        if (isInteger) binWidth = Math.max(1, Math.ceil(binWidth));
      }

      // Sanity clamp
      binCount = Math.min(50, Math.max(3, binCount));

      const makeBinLabel = (binMin) => {
        if (isInteger) return `${Math.round(binMin)}`;
        return `${binMin.toFixed(2)}`;
      };

      if (grouping) {
        // Comparison histogram: bin per group
        const groups = [...new Set(data.map(r => String(r[grouping] || 'Unknown')))];

        const bins = Array.from({ length: binCount }, (_, i) => {
          const binMin = min + i * binWidth;
          const entry = { bin: makeBinLabel(binMin), _sort: binMin };
          groups.forEach(g => { entry[g] = 0; });
          return entry;
        });

        data.forEach(row => {
          const raw = row[col];
          const v = typeof raw === 'number' ? raw : Number(raw);
          if (isNaN(v)) return;
          let binIdx = Math.floor((v - min) / binWidth);
          if (binIdx >= binCount) binIdx = binCount - 1;
          if (binIdx < 0) binIdx = 0;
          const g = String(row[grouping] || 'Unknown');
          bins[binIdx][g]++;
        });

        return bins;
      }

      // Simple (non-grouped) histogram
      const bins = Array.from({ length: binCount }, (_, i) => {
        const binMin = min + i * binWidth;
        return {
          bin: makeBinLabel(binMin),
          count: 0,
          _sort: binMin
        };
      });

      allValues.forEach(v => {
        let binIdx = Math.floor((v - min) / binWidth);
        if (binIdx >= binCount) binIdx = binCount - 1;
        if (binIdx < 0) binIdx = 0;
        bins[binIdx].count++;
      });

      return bins;
    }

    if (!grouping) return data;

    // Transform long format to wide format for grouping
    const pivoted = data.reduce((acc, row) => {
      const xVal = row[x];
      const groupVal = row[grouping];
      const yVal = row[y];

      if (xVal === undefined || xVal === null) return acc;

      let existing = acc.find(r => String(r[x]) === String(xVal));
      if (!existing) {
        existing = { [x]: xVal };
        acc.push(existing);
      }
      if (groupVal !== undefined && groupVal !== null) {
        existing[groupVal] = yVal;
      }
      return acc;
    }, []);

    // Sort by X axis
    return pivoted.sort((a, b) => {
      const valA = a[x];
      const valB = b[x];
      if (typeof valA === 'number' && typeof valB === 'number') return valA - valB;
      return String(valA).localeCompare(String(valB), undefined, { numeric: true });
    });
  }, [data, spec]);

  const series = useMemo(() => {
    if (!data || !Array.isArray(data) || !spec) return [];
    const { y, y_columns, grouping } = spec;
    if (grouping) {
      return [...new Set(data.map(row => String(row[grouping] || 'Unknown')))];
    }
    return y_columns || [y];
  }, [data, spec]);

  if (!data || !spec) return null;

  const { plot_type, x, title, xlabel, ylabel } = spec;

  const renderChart = () => {
    // Handle empty data for all chart types
    const isEmpty = plot_type === 'sankey'
      ? (!processedData || !processedData.nodes || processedData.nodes.length === 0 || !processedData.links || processedData.links.length === 0)
      : (!processedData || processedData.length === 0);

    if (isEmpty) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <ChartIcon size={24} className="mb-2 opacity-20" />
          <p className="text-[10px] font-bold uppercase tracking-widest">No data to display</p>
        </div>
      );
    }

    // Sankey doesn't need axes
    if (plot_type === 'sankey') {
      return <SankeyTemplate data={processedData} />;
    }

    const xAxisKey = plot_type === 'hist' ? 'bin' : x;
    const seriesKeys = plot_type === 'hist'
      ? (spec.grouping
        ? [...new Set(data.map(row => String(row[spec.grouping] || 'Unknown')))]
        : ['count'])
      : series;

    const xAxis = (
      <XAxis
        dataKey={xAxisKey}
        axisLine={false}
        tickLine={false}
        tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
        dy={5}
        label={xlabel ? {
          value: xlabel,
          position: 'insideBottom',
          offset: -10,
          fontSize: 9,
          fontWeight: 800,
          fill: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        } : null}
      />
    );

    const yAxis = (
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
        dx={-5}
        label={ylabel ? {
          value: ylabel,
          angle: -90,
          position: 'insideLeft',
          offset: -30,
          fontSize: 9,
          fontWeight: 800,
          fill: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        } : null}
      />
    );

    const templateProps = {
      data: processedData,
      x: xAxisKey,
      series: seriesKeys,
      xAxis,
      yAxis,
      colors: CHART_COLORS,
      hiddenSeries,
      handleLegendClick
    };

    if (plot_type === 'bar') return <BarTemplate {...templateProps} />;
    if (plot_type === 'line') return <LineTemplate {...templateProps} />;
    if (plot_type === 'scatter') return <ScatterTemplate {...templateProps} />;
    if (plot_type === 'hist') return <HistogramTemplate {...templateProps} />;

    return <div className="text-center p-4 text-gray-400 text-xs">Unsupported plot type: {plot_type}</div>;
  };

  const chart = (
    <ResponsiveContainer width="100%" height="100%" minHeight={0} minWidth={0}>
      {renderChart()}
    </ResponsiveContainer>
  );

  if (noContainer) return chart;

  return (
    <div className="h-56 w-full mt-4 p-2 bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
      <h4 className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2 text-center">{title}</h4>
      {chart}
    </div>
  );
});
